import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import * as URI from 'urijs';

import { getUbiServicesEndpoint } from '@sitegen/common/utils/ubi-services';
import {
    ERROR_CODES,
    HTTP_CODES,
    V2_CLAIM_CODE_ENDPOINT,
    V2_SELECT_BENEFIT_ENDPOINT
} from './redemption.constants';
import {
    BenefitsResponse,
    RedeemServiceError,
    SelectBenefitResponse
} from '@sitegen/common/types/redemption.types';

export interface IRedeemServiceConfiguration {
    readonly applicationId: string;
    readonly ticket: string;
    readonly sessionId: string;
    readonly profileId: string;
}

export interface ISelectBenefitRequest {
    readonly code: string;
    readonly profileId: string;
    readonly envName: string;
}

export interface IBenefitsRequest {
    readonly code: string;
    readonly platformType?: string;
    readonly profileId: string;
    readonly envName: string;
}

export class RedeemService {
    private static AXIOS_INSTANCE: AxiosInstance;
    public static isRedeemServiceError = (errData: object) => ('httpCode' in errData && 'errorCode' in errData);

    public static configure(options: IRedeemServiceConfiguration): void {
        const headers: object = {
            'Content-Type': 'application/json',
            'Ubi-AppId': options.applicationId,
            'Ubi-SessionId': options.sessionId,
            Authorization: `Ubi_v1 t=${options.ticket}`
        };

        this.AXIOS_INSTANCE = axios.create({ headers });
    }

    public static async getSelectableBenefits(options: ISelectBenefitRequest): Promise<SelectBenefitResponse | RedeemServiceError> {
        const url: string = this.getSelectableBenefitsURL(options).href();
        const response = await this.request<SelectBenefitResponse>({ url, method: 'GET' });
        return response;
    }

    private static getSelectableBenefitsURL(options: ISelectBenefitRequest): URI {
        const host: string = getUbiServicesEndpoint(options.envName);
        const values: object = { code: options.code, profileId: options.profileId };
        const template: string = URI.expand!(V2_SELECT_BENEFIT_ENDPOINT, values).toString();
        return new URI(host).segment(template);
    }

    public static async getBenefits(options: IBenefitsRequest): Promise<BenefitsResponse | RedeemServiceError> {
        const url: string = this.getBenefitsURL(options).href();
        const { code, platformType } = options;

        const response = await this.request<BenefitsResponse>({
            url,
            method: 'POST',
            data: { code, platformType }
        });
        return response;
    }

    private static getBenefitsURL(options: IBenefitsRequest): URI {
        const host: string = getUbiServicesEndpoint(options.envName);
        const values: object = { profileId: options.profileId };
        const template: string = URI.expand!(V2_CLAIM_CODE_ENDPOINT, values).toString();
        return new URI(host).segment(template);
    }

    private static async request<T>(options: AxiosRequestConfig): Promise<T | RedeemServiceError> {
        try {
            const result: T = await this.AXIOS_INSTANCE.request({ ...options, responseType: 'json' })
                .then((response: AxiosResponse<T>): T => {
                    return response.data;
                });
            return Promise.resolve(result);
        } catch (error) {
            if (RedeemService.isRedeemServiceError(error.response.data)) {
                return Promise.reject(error.response.data);
            }
            return {
                httpCode: HTTP_CODES.SERVER_ERROR,
                errorCode: ERROR_CODES.INTERNAL_SERVER_ERROR,
                message: '',
                errorContext: '',
                transactionTime: '',
                transactionId: ''
            };
        }
    }
}

export default RedeemService;
